import { useState, useEffect, useRef } from "react";
import { Clock, CheckCircle, XCircle, ArrowLeft, AlertTriangle, Trophy, RotateCcw, Download } from "lucide-react";
import api from "@/lib/api";

export default function QuizPlayer({ quiz, onClose }) {
  const [questions, setQuestions] = useState([]);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const timerRef = useRef(null);

  useEffect(() => {
    if (quiz) {
      setQuestions(quiz.questions || []);
      setTimeLeft(quiz.time_limit_minutes * 60);
      setAnswers({});
      setCurrentQ(0);
      setSubmitted(false);
      setResult(null);
    }
  }, [quiz]);

  // Timer
  useEffect(() => {
    if (submitted || !quiz) return;
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitted, quiz]);

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  const handleAnswer = (qIdx, optIdx) => {
    if (submitted) return;
    setAnswers({ ...answers, [String(qIdx)]: optIdx });
  };

  const handleSubmit = async () => {
    if (submitted) return;
    clearInterval(timerRef.current);
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post(`/quizzes/${quiz.id}/submit`, { answers });
      setResult(data);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to submit quiz");
    } finally {
      setLoading(false);
    }
  };

  const answeredCount = Object.keys(answers).length;
  const totalQ = questions.length;
  const isLastQ = currentQ === totalQ - 1;
  const isUrgent = timeLeft <= 60;

  // Result Screen
  if (submitted && result) {
    return (
      <div className="fixed inset-0 z-[999] bg-slate-900 overflow-y-auto" data-testid="quiz-result-screen">
        <div className="min-h-screen flex flex-col">
          {/* Header */}
          <div className="px-4 py-3 bg-black/50 flex items-center gap-3">
            <button onClick={onClose} className="text-white" data-testid="quiz-result-close"><ArrowLeft size={20} /></button>
            <h1 className="text-sm font-bold text-white truncate">Quiz Results</h1>
          </div>

          {/* Score Card */}
          <div className="px-4 py-6 text-center">
            <div className={`w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center ${result.passed ? "bg-green-500" : "bg-red-500"}`}>
              {result.passed ? <Trophy size={36} className="text-white" /> : <XCircle size={36} className="text-white" />}
            </div>
            <h2 className="text-2xl font-black text-white">{result.score}/{result.total}</h2>
            <p className="text-lg font-bold text-white mt-1">{result.percentage}%</p>
            <span className={`inline-block mt-2 text-xs font-bold px-3 py-1 rounded ${result.passed ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}`}>
              {result.passed ? "PASSED" : "FAILED"} (Pass: {result.pass_percentage}%)
            </span>
            <p className="text-xs text-slate-500 mt-2">Attempt {result.attempts_used} of {result.max_attempts}</p>
          </div>

          {/* Answer Review */}
          <div className="px-4 pb-24">
            <h3 className="text-sm font-bold text-white mb-3">Answer Review</h3>
            {result.results?.map((r, i) => (
              <div key={i} className={`mb-3 rounded-lg p-4 ${r.is_correct ? "bg-green-500/10 border border-green-500/30" : "bg-red-500/10 border border-red-500/30"}`} data-testid={`result-q-${i}`}>
                <div className="flex items-start gap-2 mb-2">
                  {r.is_correct ? <CheckCircle size={16} className="text-green-400 mt-0.5 flex-shrink-0" /> : <XCircle size={16} className="text-red-400 mt-0.5 flex-shrink-0" />}
                  <p className="text-sm text-white font-medium">Q{i + 1}. {r.question_text}</p>
                </div>
                <div className="ml-6 space-y-1">
                  {r.options?.map((opt, j) => {
                    const isCorrect = j === r.correct_option;
                    const isUserPick = j === r.user_answer;
                    return (
                      <div key={j} className={`text-xs px-3 py-1.5 rounded flex items-center gap-2 ${isCorrect ? "bg-green-500/20 text-green-300 font-bold" : isUserPick && !isCorrect ? "bg-red-500/20 text-red-300" : "text-slate-400"}`}>
                        <span className="w-5 h-5 rounded-full border flex items-center justify-center text-[10px] flex-shrink-0">{String.fromCharCode(65 + j)}</span>
                        {opt}
                        {isCorrect && <CheckCircle size={12} className="ml-auto text-green-400" />}
                        {isUserPick && !isCorrect && <XCircle size={12} className="ml-auto text-red-400" />}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Bottom Action */}
          <div className="fixed bottom-0 left-0 right-0 p-4 bg-slate-900 border-t border-slate-800 flex gap-2">
            {result.passed && (
              <button
                onClick={async () => {
                  try {
                    const response = await api.get(`/certificates/quiz/${quiz.id}`, { responseType: "blob" });
                    const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
                    const link = document.createElement("a");
                    link.href = url;
                    link.download = `certificate_${quiz.title?.replace(/\s/g, "_") || "quiz"}.pdf`;
                    link.click();
                    window.URL.revokeObjectURL(url);
                  } catch (err) {
                    alert("Failed to download certificate");
                  }
                }}
                className="flex-1 bg-amber-500 text-white font-bold py-3 rounded-lg text-sm flex items-center justify-center gap-2"
                data-testid="download-certificate-btn"
              >
                <Download size={16} /> Certificate
              </button>
            )}
            <button onClick={onClose} className={`${result.passed ? "flex-1" : "w-full"} bg-[#0284C7] text-white font-bold py-3 rounded-lg text-sm`} data-testid="quiz-done-btn">Done</button>
          </div>
        </div>
      </div>
    );
  }

  // Quiz Player
  const q = questions[currentQ];

  return (
    <div className="fixed inset-0 z-[999] bg-slate-900 flex flex-col" data-testid="quiz-player">
      {/* Header with timer */}
      <div className="px-4 py-3 bg-black/50 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={() => { if (window.confirm("Leave quiz? Your progress will be lost.")) { clearInterval(timerRef.current); onClose(); } }} className="text-white" data-testid="quiz-leave-btn">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-sm font-bold text-white truncate">{quiz?.title}</h1>
            <p className="text-[10px] text-slate-400">{answeredCount}/{totalQ} answered</p>
          </div>
        </div>
        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg ${isUrgent ? "bg-red-500 animate-pulse" : "bg-slate-800"}`}>
          <Clock size={14} className="text-white" />
          <span className="text-sm font-bold text-white font-mono">{formatTime(timeLeft)}</span>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1 bg-slate-800">
        <div className="h-full bg-[#0284C7] transition-all" style={{ width: `${((currentQ + 1) / totalQ) * 100}%` }} />
      </div>

      {error && (
        <div className="mx-4 mt-3 bg-red-500/20 border border-red-500/30 rounded-lg p-3 flex items-center gap-2">
          <AlertTriangle size={14} className="text-red-400" />
          <p className="text-xs text-red-300">{error}</p>
        </div>
      )}

      {/* Question */}
      {q && (
        <div className="flex-1 overflow-y-auto px-4 py-6">
          <p className="text-[10px] text-[#0284C7] font-bold mb-2">QUESTION {currentQ + 1} of {totalQ}</p>
          <p className="text-base font-bold text-white leading-relaxed mb-6">{q.question_text}</p>

          {/* Options */}
          <div className="space-y-3">
            {q.options?.map((opt, j) => {
              const isSelected = answers[String(currentQ)] === j;
              return (
                <button
                  key={j}
                  data-testid={`option-${j}`}
                  onClick={() => handleAnswer(currentQ, j)}
                  className={`w-full flex items-center gap-3 p-4 rounded-lg text-left transition-none border-2 ${isSelected ? "border-[#0284C7] bg-[#0284C7]/10" : "border-slate-700 bg-slate-800/50"}`}
                >
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${isSelected ? "bg-[#0284C7] text-white" : "bg-slate-700 text-slate-300"}`}>
                    {String.fromCharCode(65 + j)}
                  </span>
                  <span className={`text-sm ${isSelected ? "text-white font-medium" : "text-slate-300"}`}>{opt}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="px-4 py-3 bg-slate-900 border-t border-slate-800 flex gap-3 flex-shrink-0">
        {currentQ > 0 && (
          <button onClick={() => setCurrentQ(currentQ - 1)} className="flex-1 bg-slate-700 text-white font-bold py-3 rounded-lg text-sm" data-testid="quiz-prev-btn">Previous</button>
        )}
        {!isLastQ ? (
          <button onClick={() => setCurrentQ(currentQ + 1)} className="flex-1 bg-[#0284C7] text-white font-bold py-3 rounded-lg text-sm" data-testid="quiz-next-btn">Next</button>
        ) : (
          <button onClick={handleSubmit} disabled={loading} className="flex-1 bg-green-600 text-white font-bold py-3 rounded-lg text-sm disabled:opacity-50" data-testid="quiz-submit-btn">
            {loading ? "Submitting..." : `Submit (${answeredCount}/${totalQ})`}
          </button>
        )}
      </div>

      {/* Question dots */}
      <div className="px-4 py-2 bg-black/30 flex gap-1.5 overflow-x-auto flex-shrink-0">
        {questions.map((_, i) => (
          <button key={i} onClick={() => setCurrentQ(i)} className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${
            i === currentQ ? "bg-[#0284C7] text-white" : answers[String(i)] !== undefined ? "bg-green-600 text-white" : "bg-slate-700 text-slate-400"
          }`}>{i + 1}</button>
        ))}
      </div>
    </div>
  );
}
